import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { DepartmentRequestType, DepartmentResponseType } from "./departmentContext";
import { useNavigate } from "react-router-dom";
import { getAuthHeaders, PATH_INFO } from "../pages/util/helper";

type DeviceType = {
  id: number,
  name: string,
  location: string,
  api_key: string,
  created_at: string,
  updated_at: string,
  created_by: string,
  updated_by: string
}

export type CompanyRequestType = {
  company_name?: string | null;
  address?: string | null;
  phone_number?: string | null;
  post_code?: number | null;
  toyoshima_sales_department?: string | null;
  toyoshima_sales_rep_name?: string | null;
  company_memo?: string | null;
  planned_site_name?: string | null;
  planned_site_address?: string | null;
  planned_site_phone?: number | null;
  planned_site_post_code?: number | null;
  planned_site_email?: string | null;
  planned_site_contact_name?: string | null;
  planned_site_contact_position?: string | null;
  contact_department?: string | null;
  contact_name?: string | null;
  contact_position?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  billing_department?: string | null;
  billing_contact?: string | null;
  billing_position?: string | null;
  billing_email?: string | null;
  billing_phone?: string | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  contract_amount?: number | null;
  departments?: DepartmentRequestType[] | null;
}

type CompanyDepartmentsResponse =
  | Partial<DepartmentResponseType>[]
  | {
    data: Partial<DepartmentResponseType>[];
    total: number;
    page: number;
    last_page: number;
  };

export type CompanyResponseType = {
  id: number,
  company_name: string,
  address: string,
  phone_number: string,
  post_code: number,
  toyoshima_sales_department: string,
  toyoshima_sales_rep_name: string,
  company_memo: string,
  planned_site_name: string,
  planned_site_address: string,
  planned_site_phone: number,
  planned_site_post_code: number,
  planned_site_email: string,
  planned_site_contact_name: string,
  planned_site_contact_position: string,
  contact_department?: string,
  contact_name?: string,
  contact_position?: string,
  contact_email?: string,
  contact_phone?: string,
  billing_department?: string,
  billing_contact?: string,
  billing_position?: string,
  billing_email?: string,
  billing_phone?: string,
  contract_start_date?: string,
  contract_end_date?: string,
  contract_amount?: number,
  created_at?: string,
  updated_at?: string,
  created_by?: string,
  updated_by?: string,
  is_disabled?: boolean,
  departments?: CompanyDepartmentsResponse,
  devices?: DeviceType[],
}

type CompanyListQuery = {
  page: number;
  limit: number;
};

type CompanyPaginationState = {
  total: number;
  page: number;
  last_page: number;
  limit: number;
};

const DEFAULT_COMPANY_LIST_LIMIT = 8;

export const CompanyContext = createContext<{
  companies: CompanyResponseType[]
  company: CompanyResponseType | null
  companyPagination: CompanyPaginationState
}>({
  companies: [],
  company: null,
  companyPagination: {
    total: 0,
    page: 1,
    last_page: 1,
    limit: DEFAULT_COMPANY_LIST_LIMIT,
  },
})

export const UpdateCompanyContext = createContext<{
  refreshCompanies: (params?: Partial<CompanyListQuery>) => Promise<void>
  registerCompany: (value: CompanyRequestType) => Promise<void>
  updateCompany: (id: string, value: CompanyRequestType) => Promise<void>
  deleteCompany: (id: number) => Promise<void>
  refreshCompany: (id: string) => Promise<void>
  toggleCompanyStatus: (id: number) => Promise<void>
}>({
  refreshCompanies: async (_params?: Partial<CompanyListQuery>) => { },
  registerCompany: async (value: CompanyRequestType) => { },
  updateCompany: async (id: string, value: CompanyRequestType) => { },
  deleteCompany: async (id: number) => { },
  refreshCompany: async (id: string) => { },
  toggleCompanyStatus: async (id: number) => { },
})

export function useCompanyContext() {
  const [companies, setCompanies] = useState<CompanyResponseType[]>([])
  const [company, setCompany] = useState<CompanyResponseType | null>(null)
  const [companyPagination, setCompanyPagination] = useState<CompanyPaginationState>({
    total: 0,
    page: 1,
    last_page: 1,
    limit: DEFAULT_COMPANY_LIST_LIMIT,
  })
  const companyQueryRef = useRef<CompanyListQuery>({ page: 1, limit: DEFAULT_COMPANY_LIST_LIMIT })
  const navigate = useNavigate()

  // 企業一覧取得
  const refreshCompanies = useCallback(async (params: Partial<CompanyListQuery> = {}) => {
    const nextQuery = { ...companyQueryRef.current, ...params }
    companyQueryRef.current = nextQuery

    try {
      const query = new URLSearchParams({
        page: String(nextQuery.page),
        limit: String(nextQuery.limit),
      })
      const endpoint = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies?${query.toString()}`

      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(endpoint, {
        headers
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await res.json()

      if (Array.isArray(data)) {
        setCompanies(data)
        setCompanyPagination({
          total: data.length,
          page: nextQuery.page,
          last_page: 1,
          limit: nextQuery.limit,
        })
      } else {
        const list = data?.data ?? []
        const total = data?.total ?? 0
        const page = data?.page ?? nextQuery.page
        const lastPage = data?.last_page ?? (nextQuery.limit > 0 ? Math.max(1, Math.ceil(total / nextQuery.limit)) : 1)

        setCompanies(list)
        setCompanyPagination({
          total,
          page,
          last_page: lastPage,
          limit: nextQuery.limit,
        })
      }
    } catch (error) {
      console.error('Error fetching companies list.', error)
    }
  }, [navigate])



  // 企業登録
  const registerCompany = useCallback(async (value: CompanyRequestType) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies`, {
        method: 'POST',
        headers,
        body: JSON.stringify(value)
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error('error res')
      }

      refreshCompanies()
    } catch (error) {
      console.error("Error fetching companies.");
    }
  }, [refreshCompanies, navigate])

  // 企業IDで企業編集
  const updateCompany = useCallback(async (id: string, value: CompanyRequestType) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(value)
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error('error res')
      }

      refreshCompanies()
    } catch (error) {
      console.error('Error update company')
    }
  }, [refreshCompanies, navigate])

  // 企業IDで詳細取得
  const refreshCompany = useCallback(async (id: string) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${id}`, {
        headers,
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await res.json();
      setCompany(data)
    } catch (error) {
      console.error("Error fetching company.");
    }
  }, [navigate])

  // 企業IDで企業削除
  const deleteCompany = useCallback(async (id: number) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${id}`, {
        method: 'DELETE',
        headers,
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      if (!res.ok) {
        throw new Error('error res')
      }

      refreshCompanies()
    } catch (error) {
      console.error("Error fetching company.")
    }
  }, [refreshCompanies, navigate])

  // 企業IDで企業ステータスを変更
  const toggleCompanyStatus = useCallback(async (id: number) => {
    try {
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/companies/${id}/toggle-disable`, {
        method: 'PATCH',
        headers,
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      refreshCompanies()
      refreshCompany(id.toString())
    } catch (error) {
      console.error("Error toggle company.")
    }
  }, [refreshCompanies, navigate, refreshCompany])

  useEffect(() => {
    refreshCompanies()
  }, [refreshCompanies])

  return {
    companies,
    company,
    companyPagination,
    refreshCompanies,
    registerCompany,
    updateCompany,
    deleteCompany,
    refreshCompany,
    toggleCompanyStatus
  }
}

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { companies, company, companyPagination, refreshCompanies, registerCompany, updateCompany, deleteCompany, refreshCompany, toggleCompanyStatus } = useCompanyContext()

  return (
    <UpdateCompanyContext.Provider value={{ refreshCompanies, registerCompany, updateCompany, deleteCompany, refreshCompany, toggleCompanyStatus }}>
      <CompanyContext.Provider value={{ companies, company, companyPagination }}>
        {children}
      </CompanyContext.Provider>
    </UpdateCompanyContext.Provider>
  )

}